import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import numeral from 'numeral'
import { useSelector } from 'react-redux'
import cn from 'classnames'

import { getCurrency } from '../../../../helpers/currencies'

import styles from './styles.module.scss'

import Table from '../../../Invoices/Table'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const DishCategoryProfitability = ({sales}) => {

  const { t } = useTranslation()
  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)
  const data = useMemo(() => {
    const categoryMap = sales.reduce((acc, sale) => {
      const categoryName = sale.DishCategory || 'Uncategorized'
      return {
        ...acc,
        [categoryName]: acc[categoryName] ? [...acc[categoryName], sale] : [sale],
      }
    }, {})
    return Object.entries(categoryMap).map(([category, sales]) => {
      const {amount, quantity, productCost} = sales.reduce((acc, sale) => {
        return {
          ...acc,
          amount: acc.amount + (sale.DishDiscountSumInt || 0),
          quantity: acc.quantity + (sale.DishAmountInt || 0),
          productCost: acc.productCost + (sale['ProductCostBase.ProductCost'] || 0),
        }
      }, {
        amount: 0,
        quantity: 0,
        productCost: 0,
      })
      return {
        name: category,
        amount: amount,
        quantity: quantity,
        productCost: productCost,
        profit: amount - productCost,
        margin: amount > 0 ? (amount - productCost) / amount * 100 : 0,
      }
    })
  }, [sales])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Category'),
      enableSorting: false,
      cell: ({ getValue }) => {
        return getValue()
      }
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Sales'),
    }),
    columnHelper.accessor('quantity', {
      id: 'quantity',
      enableSorting: false,
      cell: ({ getValue }) => numeral(getValue()).format('0.[00]'),
      header: t('Quantity'),
    }),
    columnHelper.accessor('productCost', {
      id: 'productCost',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Cost'),
    }),
    columnHelper.accessor('profit', {
      id: 'profit',
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue()
        return (
          <span className={cn({[styles.positive]: value > 0, [styles.negative]: value < 0})}>{ formatNumber(value, currency.symbol) }</span>
        )
      },
      header: t('Profit'),
    }),
    columnHelper.accessor('margin', {
      id: 'margin',
      enableSorting: false,
      cell: ({ getValue }) => `${numeral(getValue()).format('0.[00]')}%`,
      header: t('Margin %'),
    }),
  ]), [t, currency.symbol])

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Dish Category Profitability')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
    </div>
  )
}

export default DishCategoryProfitability
